import { computed, inject, Injectable } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import {
  findRankingFilterIndex,
  FixtureMatchStats,
  fixturesEvents,
  groupsEvents,
  hallOfFameEvents,
  MATCH_CONTEXT,
  matchdayWinnersEvents,
  PREDICTION_GAME_DISPLAY_CONFIG,
  rankingEvents,
  toCompetitionFilterOptions,
} from '@kikkz/shared/prediction-game/util';
import { FixturesStore } from '../store/fixtures.store';
import { RankingStore } from '../store/ranking.store';
import { GroupsStore } from '../store/groups.store';
import { HallOfFameStore } from '../store/hall-of-fame.store';
import { MatchdayWinnersStore } from '../store/matchday-winners.store';

/**
 * Single entry point of the prediction game for the feature layer, composed over the
 * region stores (fixtures, ranking, groups, hall of fame, matchday winners).
 *
 * Besides passing the stores' signals through, it owns the derivations the tabs used to
 * repeat themselves: the combined loading flags and everything the competition-filter
 * modal needs (its option list, the selected index and the button label). Selecting a
 * filter only dispatches — the stores decide what to reload.
 */
@Injectable()
export class PredictionGameFacade {
  private readonly fixturesStore = inject(FixturesStore);
  private readonly rankingStore = inject(RankingStore);
  private readonly groupsStore = inject(GroupsStore);
  private readonly hallOfFameStore = inject(HallOfFameStore);
  private readonly matchdayWinnersStore = inject(MatchdayWinnersStore);
  private readonly matchContext = inject(MATCH_CONTEXT);
  private readonly displayConfig = inject(PREDICTION_GAME_DISPLAY_CONFIG);
  private readonly fixturesDispatch = injectDispatch(fixturesEvents);
  private readonly rankingDispatch = injectDispatch(rankingEvents);
  private readonly groupsDispatch = injectDispatch(groupsEvents);
  private readonly hallOfFameDispatch = injectDispatch(hallOfFameEvents);
  private readonly matchdayWinnersDispatch = injectDispatch(matchdayWinnersEvents);

  // --- fixtures ------------------------------------------------------------
  readonly fixtures = this.fixturesStore.fixtures;
  readonly players = this.fixturesStore.players;
  readonly gameScoreValues = this.fixturesStore.gameScoreValues;
  readonly selectedFixture = this.fixturesStore.selectedFixture;
  readonly selectedFixtureIndex = this.fixturesStore.selectedFixtureIndex;
  readonly isLoadingFixtures = this.fixturesStore.isLoadingFixtures;
  readonly hasLoadedFixtures = this.fixturesStore.hasLoadedFixtures;
  readonly isLoadingPlayers = this.fixturesStore.isLoadingPlayers;
  readonly isLoadingGameScoreValues = this.fixturesStore.isLoadingGameScoreValues;
  readonly isSavingPrediction = this.fixturesStore.isSavingPrediction;
  readonly isPersistingPrediction = this.fixturesStore.isPersistingPrediction;

  // --- ranking -------------------------------------------------------------
  readonly rankingFilterOptions = this.rankingStore.rankingFilterOptions;
  readonly isLoadingRankingFilterOptions = this.rankingStore.isLoadingRankingFilterOptions;
  readonly ranking = this.rankingStore.ranking;
  readonly isLoadingRanking = this.rankingStore.isLoadingRanking;
  readonly selectedRankingUser = this.rankingStore.selectedRankingUser;
  /** Correlates a detail lookup with its outcome — see `rankingUserStatus`. */
  readonly rankingUserRequestId = this.rankingStore.rankingUserRequestId;
  readonly rankingUserStatus = this.rankingStore.rankingUserStatus;

  // --- groups --------------------------------------------------------------
  readonly userGroups = this.groupsStore.userGroups;
  readonly isLoadingUserGroups = this.groupsStore.isLoadingUserGroups;
  readonly isJoiningGroup = this.groupsStore.isJoiningGroup;
  readonly isCreatingGroup = this.groupsStore.isCreatingGroup;
  readonly isLeavingGroup = this.groupsStore.isLeavingGroup;
  readonly groupRankingFilterOptions = this.groupsStore.groupRankingFilterOptions;
  readonly isLoadingGroupRankingFilterOptions = this.groupsStore.isLoadingGroupRankingFilterOptions;
  readonly selectedGroup = this.groupsStore.selectedGroup;
  readonly selectedGroupId = this.groupsStore.selectedGroupId;
  readonly isLoadingGroupDetail = this.groupsStore.isLoadingGroupDetail;

  // --- hall of fame --------------------------------------------------------
  readonly hallOfFameStats = this.hallOfFameStore.stats;
  readonly isLoadingHallOfFame = this.hallOfFameStore.isLoadingHallOfFame;

  // --- matchday winners ----------------------------------------------------
  /** Evaluated fixtures with at least one winner, newest kick-off first. */
  readonly matchdayWinners = this.matchdayWinnersStore.matchdayWinners;
  readonly isLoadingMatchdayWinners = this.matchdayWinnersStore.isLoadingMatchdayWinners;

  // Per-section error signals — each data region surfaces its own failure independently.
  readonly fixturesError = this.fixturesStore.fixturesError;
  readonly savePredictionError = this.fixturesStore.savePredictionError;
  /** The fixture list, the squad or the scoring config failed to load — the match cannot be shown completely. */
  readonly matchDataLoadFailed = computed(
    () =>
      !!this.fixturesStore.fixturesError() ||
      !!this.fixturesStore.playersError() ||
      !!this.fixturesStore.gameScoreValuesError(),
  );
  readonly rankingError = this.rankingStore.rankingError;
  readonly userGroupsError = this.groupsStore.userGroupsError;
  readonly groupDetailError = this.groupsStore.groupDetailError;
  readonly hallOfFameError = this.hallOfFameStore.hallOfFameError;
  readonly matchdayWinnersError = this.matchdayWinnersStore.matchdayWinnersError;

  // --- derived: pinned fixture ---------------------------------------------
  /**
   * The host demanded a specific fixture (`requirePinnedFixture`) and the loaded fixtures
   * do not contain it. Only reported once the fixtures arrived successfully, so neither a
   * pending, a not yet requested nor a failed load looks like a missing match. Same match rule as
   * `findInitialFixtureIndex`; no requested id counts as missing.
   */
  readonly pinnedFixtureMissing = computed(() => {
    if (!this.displayConfig.requirePinnedFixture) return false;
    if (!this.hasLoadedFixtures() || this.isLoadingFixtures() || this.fixturesError()) return false;
    const id = this.matchContext.heimspielId();
    return !id || !this.fixtures().some((fixture) => fixture.providerId === id || fixture.id === id);
  });

  // --- derived: ranking filter ---------------------------------------------
  /**
   * Entries of the competition-filter modal for the global ranking: exactly what the
   * server delivered, never more. The backend resolves a ranking only by an option it
   * handed out itself, so a synthesised entry here would render a filter that 404s the
   * moment it is picked.
   */
  readonly rankingFilterModalOptions = computed(() => toCompetitionFilterOptions(this.rankingFilterOptions()));
  readonly selectedRankingFilterOption = this.rankingStore.selectedRankingFilterOption;
  /** Index of the selected option — several options can share one competition value. */
  readonly selectedRankingFilterIndex = computed(() =>
    findRankingFilterIndex(this.rankingFilterOptions(), this.selectedRankingFilterOption()),
  );
  /**
   * Label of the filter trigger — the server's own title, or empty while no option is
   * selected. Shared code must not invent a German fallback like "Saison": the title is
   * the backend's wording (and part of the request identity), not this layer's to guess.
   */
  readonly selectedRankingFilterLabel = computed(() => this.selectedRankingFilterOption()?.title ?? '');
  /** The selected option's server-provided second line (e.g. `'14. Spieltag'`); `null` = none. */
  readonly selectedRankingFilterSubtitle = computed(() => this.selectedRankingFilterOption()?.subtitle ?? null);
  /** The ranking view waits for the filter options first, then for the ranking itself. */
  readonly isRankingViewLoading = computed(() => this.isLoadingRanking() || this.isLoadingRankingFilterOptions());

  // --- derived: group ranking filter ----------------------------------------
  /** As above: only the options the server delivered for this group. */
  readonly groupFilterModalOptions = computed(() => toCompetitionFilterOptions(this.groupRankingFilterOptions()));
  readonly selectedGroupFilterOption = this.groupsStore.selectedGroupFilterOption;
  readonly selectedGroupFilterIndex = computed(() =>
    findRankingFilterIndex(this.groupRankingFilterOptions(), this.selectedGroupFilterOption()),
  );
  /** The detail view waits for the group's filter options first, then for its ranking. */
  readonly isGroupDetailLoading = computed(
    () => this.isLoadingGroupDetail() || this.isLoadingGroupRankingFilterOptions(),
  );

  // --- fixtures actions ----------------------------------------------------
  loadFixtures(): void {
    this.fixturesDispatch.loadFixtures();
  }

  loadPlayers(): void {
    this.fixturesDispatch.loadPlayers();
  }

  loadGameScoreValues(): void {
    this.fixturesDispatch.loadGameScoreValues();
  }

  savePrediction(fixtureId: string, prediction: FixtureMatchStats): void {
    this.fixturesDispatch.savePrediction({ fixtureId, prediction });
  }

  selectFixture(index: number): void {
    this.fixturesDispatch.selectFixture({ index });
  }

  // --- ranking actions -----------------------------------------------------
  /** Starts the ranking view: options first, the store then loads the first option's ranking. */
  loadRankingFilterOptions(): void {
    this.rankingDispatch.loadRankingFilterOptions();
  }

  /** Selection by index — the identity the competition-filter modal reports back. */
  selectRankingFilterIndex(index: number): void {
    const option = this.rankingFilterOptions()[index];
    if (!option) return;
    this.rankingDispatch.selectFilterOption({ option });
  }

  /**
   * Retry entry point of the ranking view. With an option selected it just reloads that
   * ranking; without one the options themselves never arrived, so the retry has to start
   * there — the store preselects and loads the ranking once they do.
   */
  reloadRanking(): void {
    const filterOption = this.selectedRankingFilterOption();
    if (!filterOption) {
      this.loadRankingFilterOptions();
      return;
    }
    this.rankingDispatch.loadRanking({ filterOption });
  }

  loadRankingUserDetail(userId: string, isCurrentUser: boolean): void {
    this.rankingDispatch.loadRankingUserDetail({ userId, isCurrentUser });
  }

  // --- groups actions ------------------------------------------------------
  loadUserGroups(): void {
    this.groupsDispatch.loadUserGroups();
  }

  joinGroup(code: string): void {
    this.groupsDispatch.joinGroup({ code });
  }

  createGroup(name: string): void {
    this.groupsDispatch.createGroup({ name });
  }

  leaveGroup(groupId: string): void {
    this.groupsDispatch.leaveGroup({ groupId });
  }

  /** Opens a group: loads its filter options; the store loads the ranking that follows. */
  openGroup(groupId: string): void {
    this.groupsDispatch.loadGroupRankingFilterOptions({ groupId });
  }

  selectGroupFilterIndex(index: number): void {
    const option = this.groupRankingFilterOptions()[index];
    if (!option) return;
    this.groupsDispatch.selectGroupFilterOption({ option });
  }

  /**
   * Retry entry point of the group detail view — same split as {@link reloadRanking}:
   * reload the ranking when an option is selected, otherwise reload the group's options.
   */
  reloadGroupDetail(): void {
    const groupId = this.selectedGroupId();
    if (!groupId) return;
    const filterOption = this.selectedGroupFilterOption();
    if (!filterOption) {
      this.groupsDispatch.loadGroupRankingFilterOptions({ groupId });
      return;
    }
    this.groupsDispatch.loadGroupDetail({ groupId, filterOption });
  }

  deselectGroup(): void {
    this.groupsDispatch.deselectGroup();
  }

  // --- hall of fame actions ------------------------------------------------
  loadHallOfFame(): void {
    this.hallOfFameDispatch.loadHallOfFame();
  }

  // --- matchday winners actions --------------------------------------------
  /** Loads (and is the retry entry point of) the "Spieltagssieger" list. */
  loadMatchdayWinners(): void {
    this.matchdayWinnersDispatch.loadMatchdayWinners();
  }
}
